import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Search, Download, Building2, MapPin, Phone, FileText,
  Check, X, RefreshCw, ChevronLeft, ChevronRight, Users,
  ExternalLink, Calendar, AlertCircle
} from 'lucide-react';

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

export const NPIRegistryPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedNPIs, setSelectedNPIs] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;

  const [searchParams, setSearchParams] = useState({
    state: '',
    city: '',
    postal_code: '',
    organization_name: '',
  });

  const handleSearch = async (page = 0) => {
    // Require at least one search parameter
    if (!searchParams.state && !searchParams.city && !searchParams.postal_code && !searchParams.organization_name) {
      toast.error('Please enter at least one search criteria');
      return;
    }

    setLoading(true);
    setCurrentPage(page);
    
    try {
      const res = await api.post('/api/npi/search', {
        ...searchParams,
        limit: pageSize,
        skip: page * pageSize,
      });
      
      setResults(res.data.results || []);
      setTotalCount(res.data.total_count || 0);
      setHasMore(res.data.has_more || false);
      setSelectedNPIs(new Set());
      
      if (res.data.total_count === 0) {
        toast.info('No DME suppliers found matching your criteria');
      } else {
        toast.success(`Found ${res.data.total_count} DME suppliers`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search NPI Registry');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedNPIs.size === results.length) {
      setSelectedNPIs(new Set());
    } else {
      setSelectedNPIs(new Set(results.map(r => r.npi)));
    }
  };

  const handleToggleSelect = (npi) => {
    const newSelected = new Set(selectedNPIs);
    if (newSelected.has(npi)) {
      newSelected.delete(npi);
    } else {
      newSelected.add(npi);
    }
    setSelectedNPIs(newSelected);
  };

  const handleImport = async () => {
    if (selectedNPIs.size === 0) {
      toast.error('Please select at least one company to import');
      return;
    }

    setImporting(true);
    try {
      const res = await api.post('/api/npi/import', {
        npi_numbers: Array.from(selectedNPIs)
      });
      
      toast.success(res.data.message);
      
      // Refresh results to show updated import status
      handleSearch(currentPage);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import leads');
    } finally {
      setImporting(false);
    }
  };

  const handleClear = () => {
    setSearchParams({
      state: '',
      city: '',
      postal_code: '',
      organization_name: '',
    });
    setResults([]);
    setTotalCount(0);
    setSelectedNPIs(new Set());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">NPI Registry</h1>
          <p className="text-gray-500">Search and import DME suppliers from the Medicare NPI database</p>
        </div>
        <a 
          href="https://npiregistry.cms.hhs.gov" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-[#014DB7] hover:underline flex items-center gap-1"
        >
          <ExternalLink className="w-4 h-4" />
          CMS NPI Registry
        </a>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-[#014DB7]" />
          Search DME Suppliers
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <Label>State</Label>
            <Select 
              value={searchParams.state || "all"} 
              onValueChange={(v) => setSearchParams({ ...searchParams, state: v === "all" ? "" : v })}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select state..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {US_STATES.map(state => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>City</Label>
            <Input
              value={searchParams.city}
              onChange={(e) => setSearchParams({ ...searchParams, city: e.target.value })}
              placeholder="e.g., Birmingham"
              className="rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label>ZIP Code</Label>
            <Input
              value={searchParams.postal_code}
              onChange={(e) => setSearchParams({ ...searchParams, postal_code: e.target.value })}
              placeholder="e.g., 35203"
              className="rounded-xl"
              maxLength={5}
            />
          </div>

          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input
              value={searchParams.organization_name}
              onChange={(e) => setSearchParams({ ...searchParams, organization_name: e.target.value })}
              placeholder="Search by name..."
              className="rounded-xl"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => handleSearch(0)}
            disabled={loading}
            className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search Registry
              </>
            )}
          </Button>
          <Button
            onClick={handleClear}
            variant="outline"
            className="rounded-xl"
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* Results Header */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedNPIs.size === results.length && results.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-gray-600">
                  {selectedNPIs.size > 0 ? `${selectedNPIs.size} selected` : 'Select all'}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount.toLocaleString()} results
              </span>
            </div>
            
            <Button
              onClick={handleImport}
              disabled={importing || selectedNPIs.size === 0}
              className="bg-[#84CC16] hover:bg-[#65a30d] text-white rounded-xl"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import as Leads ({selectedNPIs.size})
                </>
              )}
            </Button>
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="w-10 py-3 px-4"></th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">NPI</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Location</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Phone</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <tr 
                    key={result.npi}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      result.already_pitched ? 'bg-orange-50/50' : result.already_imported ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <Checkbox
                        checked={selectedNPIs.has(result.npi)}
                        onCheckedChange={() => handleToggleSelect(result.npi)}
                        disabled={result.already_pitched}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#014DB7]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-[#014DB7]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0A1628]">{result.company_name}</p>
                          {result.contact_name && (
                            <p className="text-sm text-gray-500">{result.contact_name}</p>
                          )}
                          {result.taxonomy_description && (
                            <p className="text-xs text-gray-400 mt-1">{result.taxonomy_description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{result.npi}</code>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-600">
                          <p>{result.city}, {result.state} {result.zip_code}</p>
                          {result.address && (
                            <p className="text-xs text-gray-400 truncate max-w-[200px]">{result.address}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {result.phone ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {result.phone}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {result.already_pitched ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          <X className="w-3 h-3" />
                          Already Pitched
                        </span>
                      ) : result.already_imported ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          <Check className="w-3 h-3" />
                          Imported
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          New
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <Button
              onClick={() => handleSearch(currentPage - 1)}
              disabled={currentPage === 0 || loading}
              variant="outline"
              className="rounded-xl"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            <span className="text-sm text-gray-500">
              Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}
            </span>
            
            <Button
              onClick={() => handleSearch(currentPage + 1)}
              disabled={!hasMore || loading}
              variant="outline"
              className="rounded-xl"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#0A1628] mb-2">Search the NPI Registry</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Enter search criteria above to find DME suppliers from the Medicare National Provider Identifier database. 
            Results can be imported directly as leads.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#014DB7] mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#014DB7]">About NPI Data</p>
            <p className="text-xs text-blue-600 mt-1">
              Data is sourced from the CMS NPPES (National Plan & Provider Enumeration System). 
              Only organizations with DME supplier taxonomy are shown. The NPI registry does not include email addresses.
              Existing leads with matching NPI numbers will be updated with the latest information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPIRegistryPage;
